/**
 * SnapshotTrail — Vertikaler Thumbnail-Trail am rechten Bildschirmrand.
 *
 * Funktioniert wie eine visuelle Scrollbar:
 * - Thumbnails als Wegmarken, über die gesamte Viewport-Höhe verteilt
 * - Ein Indikator-Punkt bewegt sich flüssig per DOM-Manipulation
 *   (kein React-State für die Position → kein Re-Render-Lag)
 * - Beim Vorbeifahren an einem Thumbnail wird dessen Szene aktiviert
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Editor } from '@tiptap/react'

interface SnapshotEntry {
  relPos: number
  label: string
  thumbnail: string | null
  snapshotJson: string
  element: HTMLElement
}

interface SnapshotTrailProps {
  editor: Editor | null
  scrollRoot: HTMLElement | null
  onSnapshotClick?: (snapshotJson: string) => void
}

const INSET = 56 // px from top/bottom viewport edge

/** Convert a normalized 0..1 value to a pixel top position */
function toPx(t: number): number {
  return INSET + t * (window.innerHeight - INSET * 2)
}

export function SnapshotTrail({ editor, scrollRoot, onSnapshotClick }: SnapshotTrailProps) {
  const [entries, setEntries] = useState<SnapshotEntry[]>([])
  const [activeIdx, setActiveIdx] = useState<number | null>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)

  // ── Collect snapshot positions ────────────────────────────────────────
  const collect = useCallback(() => {
    if (!editor || !scrollRoot) { setEntries([]); return }

    const dom = editor.view.dom as HTMLElement
    const els = dom.querySelectorAll<HTMLElement>('[data-type="view-snapshot"]')
    if (els.length === 0) { setEntries([]); return }

    const scrollH = scrollRoot.scrollHeight
    if (scrollH <= 0) { setEntries([]); return }

    const rootRect = scrollRoot.getBoundingClientRect()
    const rootScrollTop = scrollRoot.scrollTop

    const list: SnapshotEntry[] = []
    els.forEach(el => {
      const elRect = el.getBoundingClientRect()
      const offsetTop = elRect.top - rootRect.top + rootScrollTop
      const relPos = Math.max(0, Math.min(1, offsetTop / scrollH))

      const label = el.getAttribute('data-label') || 'Ansicht'
      const snapshotJson = el.getAttribute('data-snapshot') || '{}'

      let thumbnail: string | null = null
      try { thumbnail = JSON.parse(snapshotJson).thumbnail || null }
      catch { /* ignore */ }

      list.push({ relPos, label, snapshotJson, thumbnail, element: el })
    })

    setEntries(list)
  }, [editor, scrollRoot])

  // Re-collect on DOM/layout changes
  useEffect(() => {
    if (!editor || !scrollRoot) return
    collect()
    const mo = new MutationObserver(collect)
    mo.observe(editor.view.dom, { childList: true, subtree: true })
    const ro = new ResizeObserver(collect)
    ro.observe(scrollRoot)
    return () => { mo.disconnect(); ro.disconnect() }
  }, [editor, scrollRoot, collect])

  // ── Scroll tracking: direct DOM for indicator, state only for activeIdx ─
  useEffect(() => {
    if (!scrollRoot || entries.length === 0) { setActiveIdx(null); return }

    // Pre-compute normalized thumbnail positions (same formula as render)
    const first = entries[0].relPos
    const last  = entries[entries.length - 1].relPos
    const span  = last - first
    const normals = entries.map(e =>
      entries.length === 1 ? 0.5
        : span < 0.001 ? 0.5
        : (e.relPos - first) / span
    )

    let prevActive = 0

    const onScroll = () => {
      const t = scrollRoot.scrollTop
      const h = scrollRoot.scrollHeight
      const c = scrollRoot.clientHeight
      const maxScroll = h - c
      const rootRect = scrollRoot.getBoundingClientRect()

      if (maxScroll <= 0) {
        if (indicatorRef.current) indicatorRef.current.style.top = toPx(normals[0]) + 'px'
        if (prevActive !== 0) { prevActive = 0; setActiveIdx(0) }
        return
      }

      // For each entry: the scrollTop that would center it in the viewport.
      // This is the anchor point — when scrollTop = anchor[i], indicator = thumb[i].
      const anchors = entries.map(e => {
        const r = e.element.getBoundingClientRect()
        const offset = r.top - rootRect.top + t
        return Math.max(0, Math.min(maxScroll, offset - c * 0.5))
      })

      // Clamp: before first anchor → first thumb, after last → last thumb
      let indicatorNormal: number
      let active: number

      if (t <= anchors[0]) {
        indicatorNormal = normals[0]
        active = 0
      } else if (t >= anchors[anchors.length - 1]) {
        indicatorNormal = normals[normals.length - 1]
        active = entries.length - 1
      } else {
        // Find which two anchors we're between
        let below = 0
        for (let i = 0; i < anchors.length; i++) {
          if (anchors[i] <= t + 0.5) below = i
        }
        const above = Math.min(below + 1, anchors.length - 1)
        active = below

        if (below === above) {
          indicatorNormal = normals[below]
        } else {
          const range = anchors[above] - anchors[below]
          const progress = range < 1 ? 0 : (t - anchors[below]) / range
          indicatorNormal = normals[below] + progress * (normals[above] - normals[below])
        }
      }

      // Move indicator directly via DOM — no React re-render
      if (indicatorRef.current) {
        indicatorRef.current.style.top = toPx(indicatorNormal) + 'px'
      }

      if (active !== prevActive) {
        prevActive = active
        setActiveIdx(active)
      }
    }

    onScroll()
    scrollRoot.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollRoot.removeEventListener('scroll', onScroll)
  }, [scrollRoot, entries])

  // ── Click handler ─────────────────────────────────────────────────────
  const handleClick = useCallback((entry: SnapshotEntry) => {
    // No manual indicator snap — the scroll handler positions the indicator
    // correctly on every frame during the smooth scroll animation.
    entry.element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    onSnapshotClick?.(entry.snapshotJson)
  }, [onSnapshotClick])

  if (entries.length === 0) return null

  // Normalize relPos: first → 0, last → 1
  const first = entries[0].relPos
  const last = entries[entries.length - 1].relPos
  const span = last - first
  const normals = entries.map(e =>
    entries.length === 1 ? 0.5
      : span < 0.001 ? 0.5
      : (e.relPos - first) / span
  )

  const trail = (
    <div className="snapshot-trail" data-component="SnapshotTrail">
      {/* Moving scroll indicator — positioned via ref, not state */}
      <div ref={indicatorRef} className="snapshot-trail__indicator" />

      {entries.map((entry, i) => {
        const isActive = activeIdx === i
        const topPx = toPx(normals[i])
        return (
          <div
            key={i}
            className={`snapshot-trail__stop${isActive ? ' is-active' : ''}`}
            style={{ top: topPx }}
            onClick={() => handleClick(entry)}
          >
            <div className="snapshot-trail__card">
              {entry.thumbnail ? (
                <img
                  className="snapshot-trail__thumb"
                  src={entry.thumbnail}
                  alt={entry.label}
                  draggable={false}
                />
              ) : (
                <div className="snapshot-trail__thumb snapshot-trail__thumb--empty" />
              )}
              <span className="snapshot-trail__label">{entry.label}</span>
            </div>
          </div>
        )
      })}
    </div>
  )

  return createPortal(trail, document.body)
}
